(() => {
  const sectionSelector = '.tnf-tech';
  const panelTransitionDuration = 360;
  const autoScrollDuration = 420;
  /** @type {WeakMap<HTMLElement, number>} */
  const panelHideTimers = new WeakMap();

  /**
   * @param {ParentNode} section
   * @returns {HTMLElement[]}
   */
  const getCards = (section) => Array.from(section.querySelectorAll('[data-tech-card]')).filter(
    /** @returns {node is HTMLElement} */
    (node) => node instanceof HTMLElement && node.dataset.techClone !== 'true'
  );

  /**
   * @param {ParentNode} section
   * @returns {HTMLElement[]}
   */
  const getTrackCards = (section) => Array.from(section.querySelectorAll('[data-tech-card]')).filter(
    /** @returns {node is HTMLElement} */
    (node) => node instanceof HTMLElement
  );

  /**
   * @param {ParentNode} section
   * @returns {HTMLElement[]}
   */
  const getDescriptionPanels = (section) => Array.from(section.querySelectorAll('[data-tech-description-panel]')).filter(
    /** @returns {node is HTMLElement} */
    (node) => node instanceof HTMLElement
  );

  /**
   * @param {ParentNode} section
   * @returns {HTMLElement[]}
   */
  const getPanels = (section) => Array.from(section.querySelectorAll('[data-tech-panel]')).filter(
    /** @returns {node is HTMLElement} */
    (node) => node instanceof HTMLElement
  );

  /** @param {HTMLElement} panel */
  const clearPanelHideTimer = (panel) => {
    const timerId = panelHideTimers.get(panel);
    if (timerId) {
      window.clearTimeout(timerId);
      panelHideTimers.delete(panel);
    }
  };

  /** @param {HTMLElement} panel */
  const showPanel = (panel) => {
    clearPanelHideTimer(panel);
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    panel.classList.remove('is-exiting');

    requestAnimationFrame(() => {
      panel.classList.add('is-visible');
    });
  };

  /** @param {HTMLElement} panel */
  const hidePanel = (panel) => {
    clearPanelHideTimer(panel);
    panel.setAttribute('aria-hidden', 'true');
    panel.classList.remove('is-visible');
    panel.classList.add('is-exiting');

    const timerId = window.setTimeout(() => {
      panel.hidden = true;
      panel.classList.remove('is-exiting');
      panelHideTimers.delete(panel);
    }, panelTransitionDuration);

    panelHideTimers.set(panel, timerId);
  };

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement[]} cards
   */
  const buildInfiniteTrack = (track, cards) => {
    if (track.dataset.techInfiniteReady === 'true' || cards.length < 2) {
      return;
    }

    const prependFragment = document.createDocumentFragment();
    const appendFragment = document.createDocumentFragment();

    cards.forEach((card) => {
      const prependClone = /** @type {HTMLElement} */ (card.cloneNode(true));
      prependClone.dataset.techClone = 'true';
      prependClone.dataset.techClonePosition = 'prepend';
      delete prependClone.dataset.active;
      prependClone.setAttribute('aria-hidden', 'true');
      prependClone.setAttribute('tabindex', '-1');
      prependClone.classList.remove('is-active');
      prependClone.removeAttribute('aria-current');
      prependClone.setAttribute('aria-pressed', 'false');
      prependFragment.appendChild(prependClone);

      const appendClone = /** @type {HTMLElement} */ (card.cloneNode(true));
      appendClone.dataset.techClone = 'true';
      appendClone.dataset.techClonePosition = 'append';
      delete appendClone.dataset.active;
      appendClone.setAttribute('aria-hidden', 'true');
      appendClone.setAttribute('tabindex', '-1');
      appendClone.classList.remove('is-active');
      appendClone.removeAttribute('aria-current');
      appendClone.setAttribute('aria-pressed', 'false');
      appendFragment.appendChild(appendClone);
    });

    track.prepend(prependFragment);
    track.append(appendFragment);
    track.dataset.techInfiniteReady = 'true';
  };

  /**
   * @param {HTMLElement} section
   * @param {string | undefined} cardId
   */
  const getCardInstancesById = (section, cardId) => {
    if (!cardId) {
      return [];
    }

    return getTrackCards(section).filter((card) => card.dataset.techCardId === cardId);
  };

  /**
   * @param {HTMLElement} track
   */
  const getTrackCenter = (track) => track.scrollLeft + track.clientWidth / 2;

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement} card
   * @returns {-1 | 0 | 1}
   */
  const getCardDirectionFromPosition = (track, card) => {
    const trackCenter = getTrackCenter(track);
    const cardCenter = card.offsetLeft + card.offsetWidth / 2;
    const delta = cardCenter - trackCenter;

    if (delta > 1) {
      return 1;
    }

    if (delta < -1) {
      return -1;
    }

    return 0;
  };

  /**
   * @param {HTMLElement[]} cards
   * @param {HTMLElement | null} fromCard
   * @param {HTMLElement | null} toCard
   * @param {HTMLElement} track
   * @returns {-1 | 0 | 1}
   */
  const getLogicalDirectionBetweenCards = (cards, fromCard, toCard, track) => {
    const fromIndex = getCardIndex(cards, fromCard);
    const toIndex = getCardIndex(cards, toCard);

    if (fromIndex === toIndex) {
      return 0;
    }

    const total = cards.length;
    const forwardDistance = (toIndex - fromIndex + total) % total;
    const backwardDistance = (fromIndex - toIndex + total) % total;

    if (forwardDistance < backwardDistance) {
      return 1;
    }

    if (backwardDistance < forwardDistance) {
      return -1;
    }

    return getCardDirectionFromPosition(track, /** @type {HTMLElement} */ (toCard));
  };

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement[]} cards
   */
  const getNearestVisibleCard = (track, cards) => {
    const trackCenter = getTrackCenter(track);
    /** @type {HTMLElement | null} */
    let nearestCard = cards[0] || null;
    let smallestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCenter - trackCenter);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        nearestCard = card;
      }
    });

    return nearestCard;
  };

  /**
   * @param {HTMLElement} section
   * @param {string | undefined} cardId
   * @param {HTMLElement} track
   * @param {-1 | 0 | 1} [direction=0]
   */
  const getPreferredCardInstance = (section, cardId, track, direction = 0) => {
    const candidates = getCardInstancesById(section, cardId);
    if (!candidates.length) {
      return null;
    }

    if (direction === 0) {
      return getNearestVisibleCard(track, candidates);
    }

    const trackCenter = getTrackCenter(track);
    const directionalCandidates = candidates
      .map((card) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        return {
          card,
          delta: cardCenter - trackCenter
        };
      })
      .filter(({ delta }) => direction > 0 ? delta > 1 : delta < -1)
      .sort((left, right) => Math.abs(left.delta) - Math.abs(right.delta));

    return directionalCandidates[0]?.card || getNearestVisibleCard(track, candidates);
  };

  /**
   * @param {HTMLElement[]} cards
   * @param {HTMLElement | null} activeCard
   */
  const getCardIndex = (cards, activeCard) => {
    if (!(activeCard instanceof HTMLElement)) {
      return 0;
    }

    const activeCardId = activeCard.dataset.techCardId;
    const activeIndex = cards.findIndex((card) => card.dataset.techCardId === activeCardId);
    return activeIndex >= 0 ? activeIndex : 0;
  };

  /**
   * @param {HTMLElement} section
   * @param {HTMLElement} displayCard
   */
  const normalizeInfinitePosition = (section, displayCard) => {
    if (displayCard.dataset.techClone !== 'true') {
      return displayCard;
    }

    const track = section.querySelector('[data-tech-track]');
    const sourceCard = getCards(section).find((card) => card.dataset.techCardId === displayCard.dataset.techCardId);

    if (!(track instanceof HTMLElement) || !(sourceCard instanceof HTMLElement)) {
      return displayCard;
    }

    track.scrollLeft -= displayCard.offsetLeft - sourceCard.offsetLeft;
    setActiveCard(section, sourceCard, { scroll: false });
    return sourceCard;
  };

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement} card
   * @param {'smooth' | 'auto'} [behavior='smooth']
   */
  const centerCardInTrack = (track, card, behavior = 'smooth') => {
    const trackRect = track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const targetLeft = track.scrollLeft + (cardRect.left - trackRect.left) - (trackRect.width - cardRect.width) / 2;
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const nextLeft = Math.max(0, Math.min(targetLeft, maxScrollLeft));

    track.scrollTo({ left: nextLeft, behavior });
  };

  /**
   * @param {HTMLElement} section
   * @param {string | undefined} cardId
   */
  const syncDescriptions = (section, cardId) => {
    const panels = getDescriptionPanels(section);
    if (!panels.length) {
      return;
    }

    panels.forEach((panel) => {
      const isActive = panel.dataset.techCardId === cardId;
      panel.classList.toggle('is-active', isActive);
      panel.setAttribute('aria-hidden', String(!isActive));
    });
  };

  /**
   * @param {HTMLElement} section
   * @param {string | undefined} groupKey
   */
  const syncPanels = (section, groupKey) => {
    if (!groupKey) {
      return;
    }

    const panels = getPanels(section);
    if (!panels.length) {
      return;
    }

    const matchingPanels = panels.filter((panel) => panel.dataset.techGroupKey === groupKey);
    if (!matchingPanels.length) {
      return;
    }

    panels.forEach((panel) => {
      const isMatch = panel.dataset.techGroupKey === groupKey;
      panel.classList.toggle('is-active', isMatch);

      if (isMatch) {
        showPanel(panel);
      } else {
        hidePanel(panel);
      }
    });
  };

  /**
   * @param {HTMLElement} section
   * @param {HTMLElement} nextCard
  * @param {{ scroll?: boolean, instant?: boolean }} [options]
   */
  const setActiveCard = (section, nextCard, options = {}) => {
    if (!(nextCard instanceof HTMLElement)) {
      return;
    }

    const { scroll = true, instant = false } = options;
    const cards = getCards(section);
    const trackCards = getTrackCards(section);
    const nextId = nextCard.dataset.techCardId;
    const nextGroupKey = nextCard.dataset.techGroupKey;

    trackCards.forEach((card) => {
      const isActive = card === nextCard;
      card.classList.toggle('is-active', isActive);

      if (card.tagName === 'A') {
        if (isActive) {
          card.setAttribute('aria-current', 'page');
        } else {
          card.removeAttribute('aria-current');
        }
      } else {
        card.setAttribute('aria-pressed', String(isActive));
      }
    });

    cards.forEach((card) => {
      const isActive = card.dataset.techCardId === nextId;

      if (isActive) {
        card.dataset.active = 'true';
      } else {
        delete card.dataset.active;
      }
    });

    if (nextId) {
      syncDescriptions(section, nextId);
    }

    if (nextGroupKey) {
      syncPanels(section, nextGroupKey);
    }

    if (scroll) {
      const track = section.querySelector('[data-tech-track]');
      if (track instanceof HTMLElement) {
        centerCardInTrack(track, nextCard, instant ? 'auto' : 'smooth');
      }
    }

    updateCarousel(section);
  };

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement[]} cards
   */
  const getNearestCard = (track, cards) => {
    return getNearestVisibleCard(track, cards);
  };

  /** @param {HTMLElement} section */
  const updateCarousel = (section) => {
    const track = section.querySelector('[data-tech-track]');
    const prevButton = section.querySelector('[data-tech-prev]');
    const nextButton = section.querySelector('[data-tech-next]');

    if (!(track instanceof HTMLElement) || !(prevButton instanceof HTMLButtonElement) || !(nextButton instanceof HTMLButtonElement)) {
      return;
    }

    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const canScroll = maxScrollLeft > 2;

    prevButton.disabled = !canScroll;
    nextButton.disabled = !canScroll;
    prevButton.hidden = !canScroll;
    nextButton.hidden = !canScroll;
  };

  /** @param {HTMLElement} track */
  const getScrollAmount = (track) => {
    const activeCard = track.querySelector('.tnf-tech__nav-card.is-active');
    const firstCard = track.querySelector('.tnf-tech__nav-card');
    const card = activeCard || firstCard;
    const trackStyles = window.getComputedStyle(track);
    const gapValue = parseFloat(trackStyles.columnGap || trackStyles.gap || '24') || 24;

    if (!card) {
      return track.clientWidth * 0.85;
    }

    return card.getBoundingClientRect().width + gapValue;
  };

  /** @param {HTMLElement} section */
  const initSection = (section) => {
    if (!(section instanceof HTMLElement) || section.dataset.techReady === 'true') {
      return;
    }

    const track = section.querySelector('[data-tech-track]');
    const prevButton = section.querySelector('[data-tech-prev]');
    const nextButton = section.querySelector('[data-tech-next]');
    const cards = getCards(section);

    if (track instanceof HTMLElement && prevButton instanceof HTMLButtonElement && nextButton instanceof HTMLButtonElement && cards.length) {
      buildInfiniteTrack(track, cards);

      /** @type {number | null} */
      let scrollTimer = null;
      /** @type {number | null} */
      let autoScrollTimer = null;
      /** @type {number | null} */
      let normalizeTimer = null;
      let isAutoScrolling = false;
      let shouldSyncFromScroll = false;

      const stopAutoScrollSync = () => {
        isAutoScrolling = false;

        if (autoScrollTimer !== null) {
          window.clearTimeout(autoScrollTimer);
          autoScrollTimer = null;
        }

        if (normalizeTimer !== null) {
          window.clearTimeout(normalizeTimer);
          normalizeTimer = null;
        }
      };

      /** @param {HTMLElement | null} card */
      const getResolvedActiveCard = (card) => {
        if (!(card instanceof HTMLElement)) {
          return null;
        }

        if (card.dataset.techClone === 'true') {
          stopAutoScrollSync();
          return normalizeInfinitePosition(section, card);
        }

        return card;
      };

      /**
       * @param {HTMLElement | null} card
       * @param {{ scroll?: boolean, instant?: boolean }} [options]
       */
      const activateCard = (card, options = {}) => {
        if (!(card instanceof HTMLElement)) {
          return;
        }

        const { scroll = true, instant = false } = options;
        shouldSyncFromScroll = false;

        if (scroll && !instant) {
          isAutoScrolling = true;

          if (autoScrollTimer !== null) {
            window.clearTimeout(autoScrollTimer);
          }

          if (normalizeTimer !== null) {
            window.clearTimeout(normalizeTimer);
          }

          autoScrollTimer = window.setTimeout(() => {
            isAutoScrolling = false;
            autoScrollTimer = null;
          }, autoScrollDuration);

          if (card.dataset.techClone === 'true') {
            normalizeTimer = window.setTimeout(() => {
              normalizeInfinitePosition(section, card);
              normalizeTimer = null;
            }, autoScrollDuration + 10);
          }
        }

        setActiveCard(section, card, { scroll, instant });
      };

      /** @returns {HTMLElement | null} */
      const getActiveCard = () => {
        const activeCard = section.querySelector('[data-tech-card].is-active');
        return activeCard instanceof HTMLElement ? activeCard : cards[0] || null;
      };

      /** @param {HTMLElement} card */
      const activateCardFromInteraction = (card) => {
        const activeCard = getResolvedActiveCard(getActiveCard());
        const direction = getLogicalDirectionBetweenCards(cards, activeCard, card, track);
        const preferredCard = getPreferredCardInstance(section, card.dataset.techCardId, track, direction) || card;
        activateCard(preferredCard);
      };

      prevButton.addEventListener('click', () => {
        const activeCard = getResolvedActiveCard(getActiveCard());
        if (!activeCard) {
          return;
        }
        const activeIndex = getCardIndex(cards, activeCard);
        const nextCard = cards[(activeIndex - 1 + cards.length) % cards.length];
        if (nextCard) {
          activateCard(getPreferredCardInstance(section, nextCard.dataset.techCardId, track, -1) || nextCard);
        }
      });

      nextButton.addEventListener('click', () => {
        const activeCard = getResolvedActiveCard(getActiveCard());
        if (!activeCard) {
          return;
        }
        const activeIndex = getCardIndex(cards, activeCard);
        const nextCard = cards[(activeIndex + 1) % cards.length];
        if (nextCard) {
          activateCard(getPreferredCardInstance(section, nextCard.dataset.techCardId, track, 1) || nextCard);
        }
      });

      getTrackCards(section).forEach((card) => {
        card.addEventListener('focus', () => {
          activateCardFromInteraction(card);
        });

        /** @param {MouseEvent} event */
        card.addEventListener('click', (event) => {
          if (card.tagName !== 'A') {
            event.preventDefault();
            activateCardFromInteraction(card);
          }
        });
      });

      /** @type {number | null} */
      let rafId = null;
      const queueUpdate = () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }

        rafId = requestAnimationFrame(() => {
          updateCarousel(section);
          rafId = null;
        });
      };

      track.addEventListener('scroll', () => {
        queueUpdate();
        if (scrollTimer !== null) {
          window.clearTimeout(scrollTimer);
        }
        scrollTimer = window.setTimeout(() => {
          if (isAutoScrolling || !shouldSyncFromScroll) {
            return;
          }

          const nearestCard = getNearestCard(track, getTrackCards(section));
          if (nearestCard) {
            activateCard(nearestCard, { scroll: false });
          }

          shouldSyncFromScroll = false;
        }, 90);
      }, { passive: true });

      track.addEventListener('pointerdown', () => {
        stopAutoScrollSync();
        shouldSyncFromScroll = true;
      }, { passive: true });

      track.addEventListener('touchstart', () => {
        stopAutoScrollSync();
        shouldSyncFromScroll = true;
      }, { passive: true });

      track.addEventListener('wheel', () => {
        stopAutoScrollSync();
        shouldSyncFromScroll = true;
      }, { passive: true });

      window.addEventListener('resize', queueUpdate);
      const initialCardCandidate = getCards(section).find((card) => card.dataset.active === 'true') || null;
      const initialCard = initialCardCandidate instanceof HTMLElement ? initialCardCandidate : cards[0] || null;
      if (initialCard) {
        activateCard(initialCard, { instant: true });
      }
      queueUpdate();
    }

    const revealTarget = section.querySelector('.tnf-tech__nav-section') || section;
    if ('IntersectionObserver' in window && revealTarget) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              section.classList.add('is-revealed');
              observer.disconnect();
            }
          });
        },
        {
          threshold: 0.18
        }
      );

      observer.observe(revealTarget);
    } else {
      section.classList.add('is-revealed');
    }

    section.dataset.techReady = 'true';
  };

  /** @param {ParentNode} [root=document] */
  const initAll = (root = document) => {
    root.querySelectorAll(sectionSelector).forEach((section) => {
      if (section instanceof HTMLElement) {
        initSection(section);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  /** @param {HTMLElement} section */
  const resetSection = (section) => {
    const track = section.querySelector('[data-tech-track]');
    if (track instanceof HTMLElement) {
      track.querySelectorAll('[data-tech-clone="true"]').forEach((clone) => clone.remove());
      delete track.dataset.techInfiniteReady;
    }
    delete section.dataset.techReady;
  };

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) {
      initAll(event.target);
    }
  });

  document.addEventListener('shopify:section:reorder', (event) => {
    if (event.target instanceof HTMLElement) {
      const section = event.target.querySelector(sectionSelector);
      if (section instanceof HTMLElement) {
        resetSection(section);
        initSection(section);
      }
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }
    const section = event.target.closest(sectionSelector);
    if (!(section instanceof HTMLElement)) {
      return;
    }

    const blockEl = event.target;
    const blockId = blockEl.dataset.techCardId || blockEl.dataset.techPanelId;

    if (!blockId) {
      return;
    }

    if (!section.dataset.techReady) {
      initSection(section);
    }

    const card = getCards(section).find((c) => c.dataset.techCardId === blockId);
    if (card) {
      setActiveCard(section, card, { scroll: true, instant: false });
      return;
    }

    const panel = getPanels(section).find((p) => p.dataset.techPanelId === blockId);
    if (panel instanceof HTMLElement) {
      const groupKey = panel.dataset.techGroupKey;
      const matchingCard = getCards(section).find((c) => c.dataset.techGroupKey === groupKey);
      if (matchingCard) {
        setActiveCard(section, matchingCard, { scroll: true, instant: false });
      }
    }
  });
})();